/**
 * British ↔ American spelling pairs, generated from the patterns that produce them.
 *
 * Sources: the pairs follow the spellings Merriam-Webster gives as standard American
 * English and the Oxford dictionaries give as standard British English, organised by
 * the regular patterns those dictionaries document (-our/-or, -re/-er, -ise/-ize,
 * -yse/-yze, -ogue/-og, doubled l before a suffix, -ll/-l, -ence/-ense, ae/oe/e). The
 * stems and suffixes below were written out by hand for this tool from those patterns;
 * no published word list was copied.
 *
 * Every derived form is listed explicitly by its suffix rather than produced by a rule,
 * because the rules have exceptions a generator would get wrong: "humour" gives
 * "humourless" but "humorous" in both varieties, "vapour" gives "vaporise" (no u) in
 * British English, and "dialogue", "monologue" and "glamour" are standard American
 * spellings too, so they are not here at all.
 *
 * Words that stay -ise in every variety (advertise, compromise, exercise, revise,
 * supervise, surprise, televise…) are simply absent, which is what keeps them safe.
 */

export type SpellingPattern =
  | "our"
  | "re"
  | "ise"
  | "yse"
  | "ogue"
  | "ll"
  | "l"
  | "ence"
  | "ae"
  | "other";

export interface SpellingPair {
  uk: string;
  us: string;
  /** Oxford spelling: -ize, British otherwise. Only where it differs from `uk`. */
  oxford?: string;
  pattern: SpellingPattern;
  /** A pair that is safe in one direction only. */
  only?: "uk-us" | "us-uk";
}

const WHITESPACE_RE = /\s+/;

/** "stem:suffix,suffix stem:…" → [stem, suffixes]. An empty suffix is the bare word. */
function entries(spec: string): [string, string[]][] {
  return spec
    .trim()
    .split(WHITESPACE_RE)
    .map((entry) => {
      const [stem, suffixes = ""] = entry.split(":");
      return [stem, suffixes.split(",")];
    });
}

/** "uk/us uk/us …" → pairs. */
function slashPairs(
  spec: string,
  pattern: SpellingPattern,
  only?: SpellingPair["only"]
): SpellingPair[] {
  return spec
    .trim()
    .split(WHITESPACE_RE)
    .map((pair) => {
      const [uk, us] = pair.split("/");
      return only ? { uk, us, pattern, only } : { uk, us, pattern };
    });
}

/* ----------------------------------------------------------------- -our / -or */

/** Stems before "ur" (British) / "r" (American): colo → colour / color. */
const OUR = `
  arbo:,s ardo: armo:,s,ed,er,ers,y,ies behavio:,s,al,ally,ism,ist,ists cando:
  clamo:,s,ed,ing colo:,s,ed,ing,ings,ful,fully,less,ist,ists,fast demeano:
  endeavo:,s,ed,ing favo:,s,ed,ing,able,ably,ite,ites,itism fervo:
  flavo:,s,ed,ing,ings,less,ful,some harbo:,s,ed,ing hono:,s,ed,ing,able,ably
  humo:,s,ed,ing,less labo:,s,ed,ing,er,ers neighbo:,s,ing,hood,hoods,ly,liness
  odo:,s,less parlo:,s ranco: rigo:,s rumo:,s,ed savio:,s savo:,s,ed,ing,y,ies
  splendo:,s succo: tumo:,s valo: vapo:,s vigo: watercolo:,s,ist,ists
`;

function ourPairs(): SpellingPair[] {
  return entries(OUR).flatMap(([stem, suffixes]) =>
    suffixes.map((suffix) => ({
      uk: `${stem}ur${suffix}`,
      us: `${stem}r${suffix}`,
      pattern: "our" as const,
    }))
  );
}

/* ------------------------------------------------------------------ -re / -er */

/** n: plural; v: -red/-ered and -ring/-ering. */
const RE = `
  cent:n,v fib:n lit:n theat:n calib:n sab:n somb: spect:n lust: meag: mit:n,v
  och:n scept:n sepulch:n goit:n tit:n reconnoit:n,v
`;

const RE_EXTRAS = `
  fibreglass/fiberglass theatregoer/theatergoer theatregoers/theatergoers
  amphitheatre/amphitheater amphitheatres/amphitheaters centrepiece/centerpiece
  centrepieces/centerpieces epicentre/epicenter epicentres/epicenters
  sombrely/somberly meagrely/meagerly lacklustre/lackluster saltpetre/saltpeter
`;

const METRE_PREFIXES = ["kilo", "centi", "milli", "deci", "nano"];
const LITRE_PREFIXES = ["kilo", "centi", "milli", "deci", "micro", "nano"];

function rePairs(): SpellingPair[] {
  const pairs: SpellingPair[] = [];
  for (const [stem, flags] of entries(RE)) {
    const forms: [string, string][] = [["re", "er"]];
    if (flags.includes("n")) {
      forms.push(["res", "ers"]);
    }
    if (flags.includes("v")) {
      forms.push(["red", "ered"], ["ring", "ering"]);
    }
    for (const [uk, us] of forms) {
      pairs.push({ uk: stem + uk, us: stem + us, pattern: "re" });
    }
  }
  for (const prefix of METRE_PREFIXES) {
    pairs.push(
      { uk: `${prefix}metre`, us: `${prefix}meter`, pattern: "re" },
      { uk: `${prefix}metres`, us: `${prefix}meters`, pattern: "re" }
    );
  }
  for (const prefix of LITRE_PREFIXES) {
    pairs.push(
      { uk: `${prefix}litre`, us: `${prefix}liter`, pattern: "re" },
      { uk: `${prefix}litres`, us: `${prefix}liters`, pattern: "re" }
    );
  }
  // "meter" and "micrometer" are also measuring instruments in British English, so an
  // American "meter" cannot be converted blind; British "metre" always can.
  pairs.push(
    ...slashPairs(
      "metre/meter metres/meters micrometre/micrometer micrometres/micrometers",
      "re",
      "uk-us"
    ),
    ...slashPairs(RE_EXTRAS, "re")
  );
  return pairs;
}

/* ---------------------------------------------------------------- -ise / -ize */

/**
 * Verb stems before -ise/-ize. Every verb gets -e, -es, -ed, -ing; flags add
 * n: -ation(s), r: -er(s), l: -ational, b: -able, m: -ement(s).
 */
const ISE = `
  accessor actual:n aggrand:m agon alphabet:n anonym:n antagon apolog atom:r author:n
  bapt brutal:n bureaucrat:n cannibal capital:n categor:n cauter central:n character:n
  civil:n colon:n,r commercial:n compartmental computer:n conceptual:n contextual:n
  critic criminal:n crystall:n custom:n democrat:n demon:n deodor:r deput dichotom
  digit:n dramat:n econom emphas empath energ:r epitom equal:r essential:n eulog
  evangel external:n factor:n familiar:n fantas fertil:n,r fictional final financial:n
  formal:n fossil:n fratern galvan general:n glamor global:n harmon:n homogen:n
  hospital:n human hybrid:n hypnot hypothes ideal:n idol immobil:n immortal immun:n
  incentiv individual:n industrial:n initial:n institutional:n intellectual internal:n
  international:n ion:n italic item jeopard legal:n legitim lemmat:n liberal:n linear
  lion local:n magnet:n marginal:n material maxim:n mechan:n medical:n memor memorial
  mesmer metabol militar:n minim:n mobil:n modern:n,r moistur:r monet:n monopol moral
  motor national:n natural:n neutral:n nominal:n normal:n operational:n optim:n,r
  organ:n,r,l ostrac oxid pasteur:n patholog:n patron penal personal:n philosoph
  plagiar polar:n politic:n polymer:n popular:n pressur:n priorit:n privat:n
  problemat:n professional:n proselyt public pulver racial:n radical:n random:n
  rational:n real:n,b recogn:b regular:n revital:n revolution romantic sanit:r satir
  scandal scrutin secular:n securit:n sensational sensit:n serial:n sexual:n social:n
  special:n stabil:n,r standard:n steril:n stigmat:n subsid summar symbol sympath:r
  synchron:n synthes:r systemat:n tantal terror theor:n token:n,r traumat trivial
  union:n urban:n util:n vandal vapor:n,r verbal victim:n visual:n vocal vulcan:n
  western:n woman:r
`;

const ISE_FLAG_ENDINGS: Record<string, string[]> = {
  n: ["ation", "ations"],
  r: ["er", "ers"],
  l: ["ational"],
  b: ["able"],
  m: ["ement", "ements"],
};

/** -ise words with a second difference, where Oxford spelling is a third form. */
const ISE_COMBOS: [string, string, string][] = [
  ["colourise", "colorize", "colourize"],
  ["colourised", "colorized", "colourized"],
  ["colourises", "colorizes", "colourizes"],
  ["colourising", "colorizing", "colourizing"],
  ["colourisation", "colorization", "colourization"],
  ["tranquillise", "tranquilize", "tranquillize"],
  ["tranquillised", "tranquilized", "tranquillized"],
  ["tranquillises", "tranquilizes", "tranquillizes"],
  ["tranquillising", "tranquilizing", "tranquillizing"],
  ["tranquilliser", "tranquilizer", "tranquillizer"],
  ["tranquillisers", "tranquilizers", "tranquillizers"],
  ["anaesthetise", "anesthetize", "anaesthetize"],
  ["anaesthetised", "anesthetized", "anaesthetized"],
  ["anaesthetises", "anesthetizes", "anaesthetizes"],
  ["anaesthetising", "anesthetizing", "anaesthetizing"],
];

function isePairs(): SpellingPair[] {
  const pairs: SpellingPair[] = [];
  for (const [stem, flags] of entries(ISE)) {
    const endings = ["e", "es", "ed", "ing"];
    for (const flag of flags) {
      endings.push(...(ISE_FLAG_ENDINGS[flag] ?? []));
    }
    for (const ending of endings) {
      const us = `${stem}iz${ending}`;
      pairs.push({ uk: `${stem}is${ending}`, us, oxford: us, pattern: "ise" });
    }
  }
  pairs.push(
    {
      uk: "recognisably",
      us: "recognizably",
      oxford: "recognizably",
      pattern: "ise",
    },
    ...ISE_COMBOS.map(([uk, us, oxford]) => ({
      uk,
      us,
      oxford,
      pattern: "ise" as const,
    }))
  );
  return pairs;
}

/* ---------------------------------------------------------------- -yse / -yze */

/**
 * Stems before -yse/-yze. -yse is not the -ise suffix, so Oxford spelling keeps it.
 * British "-yses" is also the plural of the noun ("the analyses show"), so those forms
 * convert American → British only; the converter flags them the other way.
 */
const YSE = "anal:r,b paral catal hydrol electrol dial:r psychoanal breathal:r";

function ysePairs(): SpellingPair[] {
  const pairs: SpellingPair[] = [];
  for (const [stem, flags] of entries(YSE)) {
    const endings = ["e", "ed", "ing"];
    if (flags.includes("r")) {
      endings.push("er", "ers");
    }
    if (flags.includes("b")) {
      endings.push("able");
    }
    for (const ending of endings) {
      pairs.push({
        uk: `${stem}ys${ending}`,
        us: `${stem}yz${ending}`,
        pattern: "yse",
      });
    }
    const plural = {
      uk: `${stem}yses`,
      us: `${stem}yzes`,
      pattern: "yse" as const,
    };
    pairs.push(stem === "breathal" ? plural : { ...plural, only: "us-uk" });
  }
  return pairs;
}

/* ---------------------------------------------------------------- -ogue / -og */

/** Only where American dictionaries prefer -og: dialogue and prologue keep -ue. */
const OGUE = `
  catalogue/catalog catalogues/catalogs catalogued/cataloged cataloguing/cataloging
  cataloguer/cataloger cataloguers/catalogers analogue/analog analogues/analogs
  homologue/homolog homologues/homologs
`;

/* --------------------------------------------------------- doubled l (travelled) */

/**
 * Verbs ending in a vowel + l, stress not on the last syllable: British doubles the l
 * before -ed and -ing, American does not. Flags: r: -ler(s); o: -lor(s); i: -list(s);
 * u: -lous.
 */
const LL = `
  barrel bevel cancel carol cavil channel chisel counsel:o cudgel dial duel:i enamel
  equal fuel funnel gambol grovel initial jewel label level:r libel:u marshal marvel:u
  model:r panel:i parcel pedal pencil quarrel revel:r rival shovel shrivel signal snivel
  snorkel spiral stencil swivel total towel trammel travel:r tunnel unravel yodel
`;

const LL_FLAG_ENDINGS: Record<string, [string, string][]> = {
  r: [
    ["ler", "er"],
    ["lers", "ers"],
  ],
  o: [
    ["lor", "or"],
    ["lors", "ors"],
  ],
  i: [
    ["list", "ist"],
    ["lists", "ists"],
  ],
  u: [["lous", "ous"]],
};

const LL_EXTRAS = `
  jewellery/jewelry jeweller/jeweler jewellers/jewelers woollen/woolen woollens/woolens
  medallist/medalist medallists/medalists dishevelled/disheveled
  marvellously/marvelously
`;

function llPairs(): SpellingPair[] {
  const pairs: SpellingPair[] = [];
  for (const [stem, flags] of entries(LL)) {
    const endings: [string, string][] = [
      ["led", "ed"],
      ["ling", "ing"],
    ];
    for (const flag of flags) {
      endings.push(...(LL_FLAG_ENDINGS[flag] ?? []));
    }
    for (const [uk, us] of endings) {
      pairs.push({ uk: stem + uk, us: stem + us, pattern: "ll" });
    }
  }
  return [...pairs, ...slashPairs(LL_EXTRAS, "ll")];
}

/* ------------------------------------------------------------ -l / -ll (enrol) */

const L = `
  enrol/enroll enrols/enrolls enrolment/enrollment enrolments/enrollments
  fulfil/fulfill fulfils/fulfills fulfilment/fulfillment fulfilments/fulfillments
  instil/instill instils/instills distil/distill distils/distills enthral/enthrall
  enthrals/enthralls appal/appall appals/appalls instalment/installment
  instalments/installments skilful/skillful skilfully/skillfully wilful/willful
  wilfully/willfully wilfulness/willfulness
`;

/* ---------------------------------------------------- -ce / -se (defence, practise) */

const ENCE = `
  defence/defense defences/defenses defenceless/defenseless offence/offense
  offences/offenses pretence/pretense pretences/pretenses practised/practiced
  practising/practicing
`;

/**
 * British spells the noun licence and practice but the verb license and practise;
 * American uses license and practice for both. So British licence and practise always
 * convert, but American license and practice depend on the part of speech.
 */
const ENCE_UK_ONLY = `
  licence/license licences/licenses practise/practice practises/practices
`;

/** A vise (American) is a vice (British) only as a tool; British vice is also a sin. */
const ENCE_US_ONLY = "vice/vise vices/vises";

/* ------------------------------------------------------------- ae / oe → e */

const AE = `
  anaemia/anemia anaemic/anemic anaesthesia/anesthesia anaesthetic/anesthetic
  anaesthetics/anesthetics anaesthetist/anesthetist anaesthetists/anesthetists
  anaesthesiology/anesthesiology anaesthesiologist/anesthesiologist
  anaesthesiologists/anesthesiologists caesarean/cesarean caesium/cesium
  coeliac/celiac diarrhoea/diarrhea dyspnoea/dyspnea apnoea/apnea faeces/feces
  faecal/fecal foetus/fetus foetuses/fetuses foetal/fetal gynaecology/gynecology
  gynaecologist/gynecologist gynaecologists/gynecologists
  gynaecological/gynecological haematology/hematology haematologist/hematologist
  haematologists/hematologists haematological/hematological haematoma/hematoma
  haematomas/hematomas haemoglobin/hemoglobin haemophilia/hemophilia
  haemorrhage/hemorrhage haemorrhages/hemorrhages haemorrhaged/hemorrhaged
  haemorrhaging/hemorrhaging haemorrhagic/hemorrhagic haemorrhoids/hemorrhoids
  haemodialysis/hemodialysis haemolysis/hemolysis haemolytic/hemolytic
  haemostasis/hemostasis haemodynamic/hemodynamic haemodynamics/hemodynamics
  ischaemia/ischemia ischaemic/ischemic leukaemia/leukemia oedema/edema
  oesophagus/esophagus oesophageal/esophageal oestrogen/estrogen oestrogens/estrogens
  oestradiol/estradiol oestrus/estrus orthopaedic/orthopedic orthopaedics/orthopedics
  paediatric/pediatric paediatrics/pediatrics paediatrician/pediatrician
  paediatricians/pediatricians palaeontology/paleontology
  palaeontologist/paleontologist palaeontologists/paleontologists
  palaeolithic/paleolithic septicaemia/septicemia hypoglycaemia/hypoglycemia
  hyperglycaemia/hyperglycemia glycaemic/glycemic hypoglycaemic/hypoglycemic
  hyperglycaemic/hyperglycemic hypoxaemia/hypoxemia homoeopathy/homeopathy
  homoeopathic/homeopathic homoeopath/homeopath homoeopaths/homeopaths
  aetiology/etiology aetiological/etiological aeon/eon aeons/eons
  paedophile/pedophile paedophiles/pedophiles paedophilia/pedophilia
  manoeuvre/maneuver manoeuvres/maneuvers manoeuvred/maneuvered
  manoeuvring/maneuvering manoeuvrable/maneuverable manoeuvrability/maneuverability
`;

/** American usage also writes these the British way, so convert only toward it. */
const AE_UK_ONLY = `
  encyclopaedia/encyclopedia encyclopaedias/encyclopedias encyclopaedic/encyclopedic
  mediaeval/medieval
`;

/** British usage never writes the American form, but American usage often writes the British one. */
const AE_US_ONLY = `
  archaeology/archeology archaeological/archeological archaeologist/archeologist
  archaeologists/archeologists aesthetic/esthetic aesthetics/esthetics
  aesthetically/esthetically
`;

/* ------------------------------------------------------------------ other */

const OTHER = `
  aluminium/aluminum grey/gray greys/grays greyer/grayer greyest/grayest
  greyish/grayish greyness/grayness greyed/grayed greying/graying plough/plow
  ploughs/plows ploughed/plowed ploughing/plowing ploughman/plowman
  snowplough/snowplow snowploughs/snowplows mould/mold moulds/molds moulded/molded
  moulding/molding mouldings/moldings mouldy/moldy smoulder/smolder smoulders/smolders
  smouldered/smoldered smouldering/smoldering moult/molt moults/molts moulted/molted
  moulting/molting pyjama/pajama pyjamas/pajamas sceptic/skeptic sceptics/skeptics
  sceptical/skeptical sceptically/skeptically scepticism/skepticism
  moustache/mustache moustaches/mustaches moustached/mustached cosy/cozy cosier/cozier
  cosiest/coziest cosily/cozily cosiness/coziness chequered/checkered
  chequebook/checkbook chequebooks/checkbooks draughty/drafty draughtsman/draftsman
  draughtsmen/draftsmen ageing/aging artefact/artifact artefacts/artifacts
  aeroplane/airplane aeroplanes/airplanes aerofoil/airfoil aerofoils/airfoils
  behove/behoove behoves/behooves behoved/behooved chilli/chili chillies/chilies
  liquorice/licorice mollusc/mollusk molluscs/mollusks pedlar/peddler
  pedlars/peddlers kerbside/curbside
`;

/**
 * Safe British → American only: the American spelling has a second meaning (a check is
 * not always a cheque, a story not always a storey, a tire not always a tyre) or is also
 * correct British usage (judgment, focused).
 */
const OTHER_UK_ONLY = `
  tyre/tire tyres/tires kerb/curb kerbs/curbs cheque/check cheques/checks
  storey/story storeys/stories programme/program programmes/programs draught/draft
  judgement/judgment judgements/judgments acknowledgement/acknowledgment
  acknowledgements/acknowledgments abridgement/abridgment sulphur/sulfur
  sulphurous/sulfurous sulphuric/sulfuric sulphate/sulfate sulphates/sulfates
  sulphide/sulfide sulphides/sulfides sulphite/sulfite sulphites/sulfites
  sulphonamide/sulfonamide sulphonamides/sulfonamides yoghurt/yogurt
  yoghurts/yogurts speciality/specialty specialities/specialties dependant/dependent
  dependants/dependents annexe/annex focussed/focused focussing/focusing
  benefitted/benefited benefitting/benefiting cypher/cipher cyphers/ciphers
`;

/** Gold purity: American karat, British carat (which is also the gem weight in both). */
const OTHER_US_ONLY = "carat/karat carats/karats";

let cached: readonly SpellingPair[] | null = null;

/** Every pair. Built once, on first use. */
export function spellingPairs(): readonly SpellingPair[] {
  if (!cached) {
    cached = [
      ...ourPairs(),
      ...rePairs(),
      ...isePairs(),
      ...ysePairs(),
      ...slashPairs(OGUE, "ogue"),
      ...llPairs(),
      ...slashPairs(L, "l"),
      ...slashPairs(ENCE, "ence"),
      ...slashPairs(ENCE_UK_ONLY, "ence", "uk-us"),
      ...slashPairs(ENCE_US_ONLY, "ence", "us-uk"),
      ...slashPairs(AE, "ae"),
      ...slashPairs(AE_UK_ONLY, "ae", "uk-us"),
      ...slashPairs(AE_US_ONLY, "ae", "us-uk"),
      ...slashPairs(OTHER, "other"),
      ...slashPairs(OTHER_UK_ONLY, "other", "uk-us"),
      ...slashPairs(OTHER_US_ONLY, "other", "us-uk"),
    ];
  }
  return cached;
}

/**
 * Words whose spelling in the other variety depends on meaning. They are highlighted
 * for the writer to decide, never changed.
 */
export type AmbiguityNote =
  | "noun-plural"
  | "draughts"
  | "practice"
  | "license"
  | "meter"
  | "program"
  | "tire"
  | "curb";

export const AMBIGUOUS_UK: Readonly<Record<string, AmbiguityNote>> = {
  analyses: "noun-plural",
  paralyses: "noun-plural",
  catalyses: "noun-plural",
  hydrolyses: "noun-plural",
  electrolyses: "noun-plural",
  dialyses: "noun-plural",
  psychoanalyses: "noun-plural",
  draughts: "draughts",
};

export const AMBIGUOUS_US: Readonly<Record<string, AmbiguityNote>> = {
  practice: "practice",
  practices: "practice",
  license: "license",
  licenses: "license",
  meter: "meter",
  meters: "meter",
  micrometer: "meter",
  micrometers: "meter",
  program: "program",
  programs: "program",
  tire: "tire",
  tires: "tire",
  curb: "curb",
  curbs: "curb",
};
